'use client';

import React from 'react';
import { IResortShortReport } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { DataTable, DataTableSort } from '@/components/common/DataTable';
import { cn } from '@/util/cn';
import { MobileDataCard } from '@/components/common/MobileDataCard';
import { ResortNameLink } from '@/components/common/ResortNameLink';
import { SectionCard } from '@/components/ui/section-card';
import { DistanceUnit } from '@/components/blocks/Unit';
import { baseDepthSortValue, baseDepthValues, hasBaseDepth } from '@/util/baseDepthRange';
import { Routes } from '@/routes';

// 72-hour forecast — first three daily-forecast entries from the API.
const forecast72hCm = (resort: IResortShortReport): number | undefined => {
  const items = resort.forecast?.slice(0, 3);
  if (!items || items.length === 0) return undefined;
  return items.reduce((sum, d) => sum + (d.snow ?? 0), 0);
};

const SORT_KEY = {
  title: 'title',
  base: 'base',
  lifts: 'lifts',
  runs: 'runs',
  last72: 'last72',
  forecast: 'forecast',
} as const;

// Numeric value backing each sortable column. Cells that render a dash
// (missing totals, zero snow depths) yield undefined so they group at
// the bottom in either direction, matching what the user sees.
const numericValue = (r: IResortShortReport, key: string): number | undefined => {
  switch (key) {
    case SORT_KEY.base: return baseDepthSortValue(r.snow, r.status?.openFlag);
    case SORT_KEY.lifts: return r.lifts?.total !== undefined ? (r.lifts.open ?? 0) : undefined;
    case SORT_KEY.runs: return r.runs?.total !== undefined ? (r.runs.open ?? 0) : undefined;
    case SORT_KEY.last72: return r.snow?.last72 || undefined;
    case SORT_KEY.forecast: return forecast72hCm(r) || undefined;
    default: return undefined;
  }
};

export const compareRows = (
  a: IResortShortReport,
  b: IResortShortReport,
  sort: DataTableSort,
): number => {
  const dirMul = sort.dir === 'asc' ? 1 : -1;
  if (sort.key === SORT_KEY.title) return a.title.localeCompare(b.title) * dirMul;

  const av = numericValue(a, sort.key);
  const bv = numericValue(b, sort.key);
  if (av === undefined && bv === undefined) return 0;
  if (av === undefined) return 1;
  if (bv === undefined) return -1;
  if (av !== bv) return (av - bv) * dirMul;
  // Equal open counts: break the tie by total (e.g. "5 / 5" vs "5 / 12").
  if (sort.key === SORT_KEY.lifts) return ((a.lifts?.total ?? 0) - (b.lifts?.total ?? 0)) * dirMul;
  if (sort.key === SORT_KEY.runs) return ((a.runs?.total ?? 0) - (b.runs?.total ?? 0)) * dirMul;
  return 0;
};

// Base depth reads as a range across the mountain — the depth at the base
// up to the depth at the summit (e.g. 20-55in). `removeEqual` collapses
// matching ends to a single figure, and only the last value in the range
// carries the unit suffix.
const BaseDepth = ({ resort, dash }: { resort: IResortShortReport, dash: string }) => {
  if (!hasBaseDepth(resort.snow, resort.status?.openFlag)) return <>{dash}</>;
  return (
    <DistanceUnit
      type="cmOrInchShort"
      separator="-"
      values={baseDepthValues(resort.snow)}
      removeEqual
    />
  );
};

// 6-col stats table used by both region/skireport (per-status section)
// and region/open-resorts (the live "Open" group). Mobile drops to a
// MobileDataCard stack with the same five stats.
//
// Desktop headers are click-to-sort (numeric columns start descending —
// most snow / most open first). Sorting is client-side, which is only
// truthful once every row is present: on a region that spans more than one
// page the caller passes `onSortIntent` to fetch the remainder first, and
// `isLoading` while that is in flight. The mobile stack keeps server order.
//
// `showRegion` appends ", Region" to each resort name — the page owns that
// call so every section of a page agrees (see util/spansMultipleRegions).
export const OpenResortsStatsTable = ({
  rows, showRegion = false, onSortIntent, isLoading = false,
}: {
  rows: IResortShortReport[];
  showRegion?: boolean;
  /**
   * Fired when a sort header is clicked. Sorting runs over the rows this
   * component holds, so on a region larger than one page the page uses this
   * to pull the rest before the order can be trusted.
   */
  onSortIntent?: () => void;
  /** Rows are still arriving; the order on screen is provisional. */
  isLoading?: boolean;
}) => {
  const { t } = useOTSTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');
  const dash = t('common.dash');

  const [sort, setSort] = React.useState<DataTableSort | null>(null);
  const handleSortChange = React.useCallback((next: DataTableSort) => {
    setSort(next);
    onSortIntent?.();
  }, [onSortIntent]);
  const sortedRows = React.useMemo(
    () => (sort ? [...rows].sort((a, b) => compareRows(a, b, sort)) : rows),
    [rows, sort],
  );

  return (
    <>
      {/* md+: the rich 6-column table. Hidden on mobile in favour of
          the MobileDataCard stack below. */}
      <div
        className={cn('hidden md:block transition-opacity', isLoading && 'opacity-60')}
        aria-busy={isLoading || undefined}
      >
        <DataTable minWidth={760} sort={sort} onSortChange={handleSortChange}>
          <DataTable.Head>
            <DataTable.HeadRow>
              {/* The five stat columns size to their (wrapping) headers;
                  claiming a share here hands the slack to the resort name
                  rather than letting it wrap, which matters most on pass
                  regions where every name carries a ", Region" suffix. */}
              <DataTable.HeadCell sortKey={SORT_KEY.title} className="w-[34%]">
                {t('region.liftTickets.resort')}
              </DataTable.HeadCell>
              <DataTable.HeadCell
                align="right"
                sortKey={SORT_KEY.base}
                defaultDir="desc"
                className="whitespace-nowrap"
              >
                {t('common.base')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.lifts} defaultDir="desc">
                {t('resort.skiReport.liftOpen')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.runs} defaultDir="desc">
                {t('resort.skiReport.runsOpen')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.last72} defaultDir="desc">
                {t('resort.skiReport.72hSnowfall')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.forecast} defaultDir="desc">
                {t('region.skiReport.snowForecast')}
              </DataTable.HeadCell>
            </DataTable.HeadRow>
          </DataTable.Head>
          <DataTable.Body>
            {sortedRows.map((r, i) => {
              const liftsOpen = r.lifts?.open;
              const liftsTotal = r.lifts?.total;
              const runsOpen = r.runs?.open;
              const runsTotal = r.runs?.total;
              const fc72 = forecast72hCm(r);
              return (
                <DataTable.Row key={r.uuid} zebra={i % 2 === 0}>
                  <DataTable.RowHeader>
                    <ResortNameLink
                      resort={r}
                      href={Routes.resortSnowReport(r)}
                      showRegion={showRegion}
                    />
                  </DataTable.RowHeader>
                  <DataTable.Cell align="right" className="whitespace-nowrap">
                    <BaseDepth resort={r} dash={dash} />
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {liftsTotal !== undefined ? (
                      <>
                        {liftsOpen ?? 0}
                        {' / '}
                        <span className="text-muted-foreground">{liftsTotal}</span>
                      </>
                    ) : dash}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {runsTotal !== undefined ? (
                      <>
                        {runsOpen ?? 0}
                        {' / '}
                        <span className="text-muted-foreground">{runsTotal}</span>
                      </>
                    ) : dash}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {r.snow?.last72 ? dt(r.snow.last72) : dash}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {fc72 ? dt(fc72) : dash}
                  </DataTable.Cell>
                </DataTable.Row>
              );
            })}
          </DataTable.Body>
        </DataTable>
      </div>

      {/* Mobile: stack the same rows as MobileDataCards so the user
          doesn't have to horizontal-scroll. */}
      <div className="md:hidden">
        <SectionCard as="div" padding="none" className="overflow-hidden">
          {rows.map((r) => {
            const liftsOpen = r.lifts?.open;
            const liftsTotal = r.lifts?.total;
            const runsOpen = r.runs?.open;
            const runsTotal = r.runs?.total;
            const fc72 = forecast72hCm(r);
            return (
              <MobileDataCard
                key={r.uuid}
                layout="grid"
                heading={(
                  <ResortNameLink
                    resort={r}
                    href={Routes.resortSnowReport(r)}
                    showRegion={showRegion}
                  />
                )}
              >
                <MobileDataCard.Stat
                  label={t('common.base')}
                  value={<BaseDepth resort={r} dash={dash} />}
                />
                <MobileDataCard.Stat
                  label={t('resort.skiReport.liftOpen')}
                  value={liftsTotal !== undefined ? (
                    <>
                      {liftsOpen ?? 0}
                      {' / '}
                      <span className="text-muted-foreground">{liftsTotal}</span>
                    </>
                  ) : dash}
                />
                <MobileDataCard.Stat
                  label={t('resort.skiReport.runsOpen')}
                  value={runsTotal !== undefined ? (
                    <>
                      {runsOpen ?? 0}
                      {' / '}
                      <span className="text-muted-foreground">{runsTotal}</span>
                    </>
                  ) : dash}
                />
                <MobileDataCard.Stat
                  label={t('resort.skiReport.72hSnowfall')}
                  value={r.snow?.last72 ? dt(r.snow.last72) : dash}
                />
                <MobileDataCard.Stat
                  label={t('region.skiReport.snowForecast')}
                  value={fc72 ? dt(fc72) : dash}
                />
              </MobileDataCard>
            );
          })}
        </SectionCard>
      </div>
    </>
  );
};
